import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { generateLeadInsights } from '../../../services/openai';
import { useToast } from '../../../components/ui/Toast';

const AIInsights = ({ insights: propInsights, onApplyRecommendation, searchData, historicalData }) => {
  const [expandedInsight, setExpandedInsight] = useState(null);
  const [insights, setInsights] = useState(Array.isArray(propInsights) ? propInsights : []);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [hasTriedAnalysis, setHasTriedAnalysis] = useState(false);
  const toast = useToast();

  // Generate AI insights when search data changes
  useEffect(() => {
    const generateInsights = async () => {
      // Don't auto-generate to prevent quota issues - only on explicit user action
      if (!searchData && !historicalData) return;
      
      // Don't auto-call API, let user trigger manually
      setHasTriedAnalysis(false);
    };

    generateInsights();
  }, [searchData, historicalData]);

  const getInsightIcon = (type) => {
    switch (type) {
      case 'optimization': return 'Target';
      case 'trend': return 'TrendingUp';
      case 'opportunity': return 'Lightbulb';
      case 'warning': return 'AlertTriangle';
      default: return 'Brain';
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'optimization': return 'text-primary bg-primary/10';
      case 'trend': return 'text-success bg-success/10';
      case 'opportunity': return 'text-warning bg-warning/10';
      case 'warning': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const toggleExpanded = (insightId) => {
    setExpandedInsight(expandedInsight === insightId ? null : insightId);
  };

  const handleRefreshInsights = async () => {
    setIsLoading(true);
    setHasTriedAnalysis(true);
    try {
      const defaultSearchData = {
        industries: ['Technology', 'SaaS'],
        locations: ['United States'],
        companySize: 'Medium (51-200)',
        roles: ['CEO', 'VP', 'Director']
      };
      const defaultHistoricalData = {
        avgResponseRate: 24.5,
        bestPerformingIndustry: 'SaaS',
        optimalOutreachTime: 'Q1',
        successfulCampaigns: ['Product Launch', 'Nurturing Series'],
        conversionTrends: { increasing: ['Technology'], stable: ['Healthcare'] }
      };
      const aiInsights = await generateLeadInsights(defaultSearchData, defaultHistoricalData);
      setInsights(aiInsights);
      setLastUpdated(new Date());
      toast?.success('AI insights refreshed successfully.', 'Insights Updated');
    } catch (error) {
      console.error('Failed to refresh AI insights:', error);
      const errorMessage = error?.message || 'Unknown error';
      const isQuotaError = errorMessage?.includes('429') || errorMessage?.includes('quota') || errorMessage?.includes('exceeded');
      const fallbackInsight = {
        id: '1',
        type: isQuotaError ? 'warning' : 'optimization',
        title: isQuotaError ? 'API Quota Exceeded' : 'AI Analysis Unavailable',
        summary: isQuotaError
          ? 'OpenAI API usage quota has been exceeded. Please upgrade your billing plan.' :'AI insights are temporarily unavailable due to API limitations',
        details: isQuotaError
          ? 'Your OpenAI API account has exceeded its usage quota. Please check your billing plan and upgrade if necessary.'
          : 'Please check your OpenAI API configuration or try again later.',
        confidence: 75,
        impact: 'Medium',
        timeframe: isQuotaError ? 'Billing Required' : 'Manual Review',
        metrics: [
          { label: 'Analysis Status', value: isQuotaError ? 'Quota Exceeded' : 'Unavailable' },
          { label: 'Next Action', value: isQuotaError ? 'Upgrade Plan' : 'Check Config' }
        ],
        recommendations: isQuotaError
          ? ['Check your OpenAI account billing and usage dashboard', 'Upgrade your API plan to increase quota limits']
          : ['Verify OpenAI API key configuration', 'Check API usage limits and billing status']
      };
      setInsights([fallbackInsight]);
      setLastUpdated(new Date());
      if (!isQuotaError) toast?.warning('AI insights unavailable. Showing fallback data.', 'AI Unavailable');
    }
    setIsLoading(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Brain" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">AI Insights</h3>
          <div className="flex items-center space-x-1 ml-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="text-xs text-muted-foreground">
              {isLoading ? 'Analyzing...' : 'AI Powered'}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefreshInsights}
          disabled={isLoading}
          iconName={isLoading ? "Loader2" : "RefreshCw"}
          className={isLoading ? "animate-spin" : ""}
        >
          {isLoading ? 'Analyzing' : 'Refresh'}
        </Button>
      </div>
      {/* Insights List */}
      <div className="p-4 space-y-4">
        {isLoading && insights?.length === 0 ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <h4 className="text-lg font-medium text-foreground mb-2">
              AI Analyzing Your Data
            </h4>
            <p className="text-muted-foreground">
              Our AI is processing your search patterns and market data to generate personalized insights...
            </p>
          </div>
        ) : insights?.length > 0 ? (
          insights?.map((insight) => (
            <div
              key={insight?.id}
              className="border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-shadow"
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${getInsightColor(insight?.type)}`}>
                  <Icon name={getInsightIcon(insight?.type)} size={16} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">{insight?.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {insight?.summary}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      {insight?.confidence && (
                        <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                          {insight?.confidence}% confidence
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpanded(insight?.id)}
                        iconName={expandedInsight === insight?.id ? "ChevronUp" : "ChevronDown"}
                      />
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedInsight === insight?.id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="space-y-3">
                        <div>
                          <h5 className="text-sm font-medium text-foreground mb-2">
                            AI Analysis
                          </h5>
                          <p className="text-sm text-muted-foreground">
                            {insight?.details}
                          </p>
                        </div>

                        {insight?.metrics && insight?.metrics?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-foreground mb-2">
                              Key Metrics
                            </h5>
                            <div className="grid grid-cols-2 gap-3">
                              {insight?.metrics?.map((metric, index) => (
                                <div key={index} className="bg-muted/50 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">{metric?.label}</p>
                                  <p className="text-sm font-medium text-foreground">
                                    {metric?.value}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {insight?.recommendations && insight?.recommendations?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-foreground mb-2">
                              AI Recommendations
                            </h5>
                            <ul className="space-y-2">
                              {insight?.recommendations?.map((rec, index) => (
                                <li key={index} className="flex items-start space-x-2">
                                  <Icon name="ArrowRight" size={14} className="text-primary mt-0.5" />
                                  <span className="text-sm text-muted-foreground">{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {insight?.action && (
                          <div className="flex items-center justify-between pt-3 border-t border-border">
                            <span className="text-sm text-muted-foreground">
                              Suggested Action
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onApplyRecommendation(insight)}
                              iconName="Play"
                            >
                              {insight?.action?.label}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick Stats */}
                  {insight?.impact && !expandedInsight && (
                    <div className="flex items-center space-x-4 mt-3 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="TrendingUp" size={12} />
                        <span>Impact: {insight?.impact}</span>
                      </div>
                      {insight?.timeframe && (
                        <div className="flex items-center space-x-1">
                          <Icon name="Clock" size={12} />
                          <span>{insight?.timeframe}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Brain" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">
              Ready to Generate AI Insights
            </h4>
            <p className="text-muted-foreground mb-4">
              Start searching for leads to get personalized AI insights and recommendations
            </p>
            <Button
              variant="outline"
              onClick={handleRefreshInsights}
              disabled={isLoading}
              iconName="Brain"
            >
              Generate Insights
            </Button>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="px-4 py-3 bg-muted/30 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Zap" size={14} />
            <span>Powered by OpenAI GPT-4</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-xs text-muted-foreground">
              Last updated: {lastUpdated?.toLocaleTimeString()}
            </span>
            <Button variant="ghost" size="sm">
              View All Insights
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInsights;